/**
 * KeyboardShortcutsHelp - Modal showing all keyboard shortcuts
 *
 * BYK-166: Keyboard shortcuts help modal (Cmd+?)
 */

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  KEYBOARD_SHORTCUTS,
  formatShortcut,
} from "@/hooks/useKeyboardShortcuts";

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Group shortcuts by category
const SHORTCUT_CATEGORIES = {
  "Panel Controls": [
    "Toggle left sidebar",
    "Toggle right sidebar",
    "Focus left sidebar",
    "Focus canvas",
    "Focus right sidebar",
    "Return focus to canvas",
  ],
  "Layout Presets": [
    "Full canvas mode",
    "Design mode",
    "Properties mode",
    "Split view",
    "AI focus mode",
  ],
  "Quick Actions": ["Open command palette", "Show keyboard shortcuts"],
};

export function KeyboardShortcutsHelp({
  open,
  onOpenChange,
}: KeyboardShortcutsHelpProps) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // Get shortcuts grouped by category
  const getShortcutsForCategory = (descriptions: string[]) => {
    return KEYBOARD_SHORTCUTS.filter((s) =>
      descriptions.includes(s.description)
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
          />

          {/* Dialog */}
          <motion.div
            className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-2xl"
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Keyboard Shortcuts
              </h2>
              <button
                onClick={() => onOpenChange(false)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="space-y-6">
              {Object.entries(SHORTCUT_CATEGORIES).map(
                ([category, descriptions]) => (
                  <div key={category}>
                    <h3 className="mb-3 text-sm font-medium text-gray-500">
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {getShortcutsForCategory(descriptions).map((shortcut) => (
                        <div
                          key={shortcut.description}
                          className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
                        >
                          <span className="text-sm text-gray-700">
                            {shortcut.description}
                          </span>
                          <kbd className="rounded bg-white px-2 py-1 font-mono text-xs text-gray-600 shadow-sm">
                            {formatShortcut(shortcut.keys as unknown as string[])}
                          </kbd>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4 text-xs text-gray-500">
              <span>
                Press{" "}
                <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                  Cmd+K
                </kbd>{" "}
                for command palette
              </span>
              <span>
                Press{" "}
                <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                  Esc
                </kbd>{" "}
                to close
              </span>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default KeyboardShortcutsHelp;
